// packages/ui/src/inputs/StableAutocomplete.tsx
'use client';

import {
  Autocomplete,
  TextField,
  Box,
  CircularProgress,
  Typography,
  type TextFieldProps,
} from '@mui/material';
import type { FilterOptionsState } from '@mui/material/useAutocomplete';
import type { SxProps, Theme } from '@mui/material/styles';
import { getFieldFillSx, sharedFieldRowSx, sharedLeftIconBoxSx } from '../forms/fields/_styles';
import type { TextFieldComponent } from '../forms/fields/FormTextField';
import type { HTMLAttributes, ReactElement, ReactNode, RefObject } from 'react';
import { deepActiveElement } from '../surface/composedTree';

/**
 * MUI's own test of whether focus is in the open list, reading the focus inside shadow roots too: in a sealed widget
 * (docs/embed-shadow-dom.md) the document reports only the shadow host, and Autocomplete took every blur for one that
 * left the list.
 */
function isActiveElementInListbox(listboxRef: RefObject<HTMLElement | null>): boolean {
  const listbox = listboxRef.current;
  return listbox !== null && listbox.parentElement?.contains(deepActiveElement(listbox.ownerDocument)) === true;
}

export type StableAutocompleteProps<T> = {
  options: T[];
  value: T | null;
  open?: boolean;

  inputValue: string;
  onChange: (v: T | null) => void;
  onInputChange: (v: string, reason?: string) => void;
  onOpen?: () => void;
  onClose?: () => void;

  label: string;
  disabled?: boolean;
  loading?: boolean;
  noOptionsText?: ReactNode;

  getOptionLabel: (opt: T) => string;
  getOptionKey?: (opt: T) => string | number;
  isOptionEqualToValue: (a: T, b: T) => boolean;
  renderOption?: (opt: T) => ReactNode;

  /** Adds a secondary line below the primary label in each dropdown option (e.g. email). */
  getOptionSecondaryLabel?: (opt: T) => string;

  /** Icon shown as a start adornment inside the text field. */
  leftIcon?: ReactNode;

  /** Pass true when options come from a server search — disables MUI's client-side filter so email/id matches aren't hidden. */
  serverSideSearch?: boolean;

  /**
   * Override MUI's client-side filter. Useful when options must match
   * against more than just `getOptionLabel` (e.g. country selector that
   * also matches a calling code). Ignored when `serverSideSearch` is
   * true.
   */
  filterOptions?: (options: T[], state: FilterOptionsState<T>) => T[];

  /** Group options by a string label. Options must be pre-sorted by group for MUI to render correctly. */
  groupBy?: (opt: T) => string;

  /**
   * Open the dropdown as soon as the field receives focus, before anything is
   * typed. Use for pickers over a bounded, browsable set (a catalog) where the
   * options themselves are the affordance — NOT for server-search fields, where
   * an empty list on focus is just noise.
   */
  openOnFocus?: boolean;

  /**
   * Highlight the first matching option as the user types, so Enter selects it
   * (type "chile", press Enter). Without it Enter does nothing until an option
   * is picked with the arrow keys or the mouse. Use for pickers over a fixed
   * list (countries, a catalog), not for `serverSideSearch` fields, where Enter
   * could pick a result left over from the previous search.
   */
  autoHighlight?: boolean;

  /**
   * Blur the input after a selection. Pair this with `openOnFocus` on any picker
   * whose selection opens a dialog: without it, the dialog's focus trap hands
   * focus back to this input, `openOnFocus` immediately re-opens the listbox,
   * and — because the listbox is portalled to `body` and mounted after the
   * dialog — it renders ON TOP of it, un-dismissable (clicking away does
   * nothing, and Esc closes the dialog underneath instead of the list).
   */
  blurOnSelect?: boolean | 'touch' | 'mouse';

  disablePortal?: boolean;
  listMaxHeightPx?: number;

  /** Highlights the field with a red background when the value is null/empty (mirrors FormTextField's highlightWhenEmpty). */
  highlightWhenEmpty?: boolean;

  /** See `getFieldFillSx`. Defaults to `'white'`. */
  background?: 'white' | 'transparent';

  /**
   * Hide MUI's built-in clear (X) button inside the input. Useful in
   * narrow layouts where the icon eats horizontal space, and for fields
   * that should never legitimately be empty (e.g. a phone-country picker
   * — the user re-selects rather than clears).
   */
  disableClearable?: boolean;

  textFieldProps?: Omit<TextFieldProps, 'label' | 'disabled' | 'InputProps'>;
  listboxSx?: SxProps<Theme>;
};

/** StableAutocomplete's own type, which `useFormAutocomplete` takes (MUI's, or the chat widget's outlined one). */
export type StableAutocompleteComponent = <T>(props: StableAutocompleteProps<T>) => ReactElement;

function StableAutocomplete<T>(props: StableAutocompleteProps<T>) {
  return useStableAutocomplete(TextField, props);
}

/**
 * StableAutocomplete, its input drawn with `TextFieldComponent` (see `useFormTextField`: the chat widget's
 * pre-chat form draws it with OutlinedTextField).
 */
function useStableAutocomplete<T>(
  TextFieldComponent: TextFieldComponent,
  {
    options,
    value,
    open,
    inputValue,
    onChange,
    onInputChange,
    onOpen,
    onClose,
    label,
    disabled,
    loading,
    noOptionsText,
    getOptionLabel,
    getOptionKey,
    isOptionEqualToValue,
    renderOption,
    getOptionSecondaryLabel,
    leftIcon,
    serverSideSearch,
    filterOptions,
    groupBy,
    disablePortal = false,
    listMaxHeightPx = 320,
    highlightWhenEmpty = false,
    background = 'white',
    disableClearable = false,
    openOnFocus = false,
    autoHighlight = false,
    blurOnSelect = false,
    textFieldProps,
    listboxSx,
  }: StableAutocompleteProps<T>,
): ReactElement {
  const resolvedRenderOption = renderOption
    ?? (getOptionSecondaryLabel
      ? (opt: T) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, py: 0.5 }}>
            <Typography variant="body2">{getOptionLabel(opt)}</Typography>
            <Typography variant="caption" color="text.secondary">{getOptionSecondaryLabel(opt)}</Typography>
          </Box>
        )
      : undefined);

  const autocomplete = (
    <Autocomplete<T, false, boolean, false>
      fullWidth
      unstable_isActiveElementInListbox={isActiveElementInListbox}
      disabled={disabled}
      disablePortal={disablePortal}
      disableClearable={disableClearable}
      openOnFocus={openOnFocus}
      autoHighlight={autoHighlight}
      blurOnSelect={blurOnSelect}
      options={options}
      value={value}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      onChange={(_, v) => onChange(v)}
      inputValue={inputValue}
      onInputChange={(_, v, reason) => onInputChange(v, reason)}
      loading={!!loading}
      noOptionsText={noOptionsText}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      getOptionKey={getOptionKey}
      filterOptions={serverSideSearch ? (x) => x : filterOptions}
      groupBy={groupBy}
      renderOption={
        resolvedRenderOption
          ? (props, option) => {
              const { key, ...rest } = props;
              const stableKey = getOptionKey ? getOptionKey(option) : key;
              return (
                <li key={stableKey} {...rest}>
                  {resolvedRenderOption(option)}
                </li>
              );
            }
          : undefined
      }
      // ✅ Allow MUI's default filtering (removes the "no filtering" override)
      slotProps={{
        listbox: {
          sx: {
            maxHeight: listMaxHeightPx,
            overflow: 'auto',
            ...((listboxSx ?? {}) as object),
          },
        } as HTMLAttributes<HTMLUListElement>,
      }}
      renderInput={(params) => {
        const { InputProps: paramsInput, inputProps: paramsHtmlInput, ...restParams } = params;
        const extraSlotProps = textFieldProps?.slotProps ?? {};

        return (
          <TextFieldComponent
            {...restParams}
            {...textFieldProps}
            label={label}
            disabled={disabled}
            sx={{ flex: 1, ...getFieldFillSx(highlightWhenEmpty && value === null, background), ...((textFieldProps?.sx ?? {}) as object) }}
            slotProps={{
              ...extraSlotProps,
              input: {
                ...paramsInput,
                ...(extraSlotProps as { input?: object }).input,
                endAdornment: (
                  <>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 26,
                        mr: 0.5,
                        flex: '0 0 auto',
                      }}
                    >
                      {loading ? <CircularProgress size={18} /> : null}
                    </Box>
                    {paramsInput.endAdornment}
                  </>
                ),
              },
              htmlInput: { ...paramsHtmlInput, ...(extraSlotProps as { htmlInput?: object }).htmlInput },
            }}
          />
        );
      }}
    />
  );

  if (leftIcon) {
    return (
      <Box sx={sharedFieldRowSx()}>
        <Box sx={sharedLeftIconBoxSx()}>
          {leftIcon}
        </Box>
        {autocomplete}
      </Box>
    );
  }

  return autocomplete;
}

export { StableAutocomplete, useStableAutocomplete };
